import React, { useState } from 'react';
import { styled, Grid, Box, Typography, FormControlLabel, Checkbox, Chip } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Table, TableBody, TableRow, TableCell } from '@mui/material';
import { useNumberInput, useToggle } from 'hooks/forms';
import {
    Section,
    Header,
    Content,
} from 'components/ui/forms';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { TextField } from 'components/ui/inputs';
import { useNotification } from 'context/notification';
import {
    addBankAccount,
    updateBankAccount,
    deleteBankAccount,
} from 'pages/stripeOnboarding/StripeOnboardingAPI';
import { ActionButton } from 'components/ui/buttons';
import ConfirmAction from 'components/popups/ConfirmAction';

const defaultModalState = {
    open: false,
    loading: false,
    bankAccount: null,
};

export default function Banking(props) {
    const { hostId, connectId, bankAccounts, readOnly, onUpdate } = props;

    const [modal, setModal] = useState(defaultModalState);

    const { notify } = useNotification();

    const createBankingDetails = () => {
        setModal((m) => ({ ...m, open: true }));
    };

    const editBankingDetails = (bankAccount) => {
        setModal((m) => ({ ...m, open: true, bankAccount }));
    };

    const handleModalClose = () => setModal(defaultModalState);

    const handleModalSubmit = (accountId, values) => {
        setModal((m) => ({ ...m, loading: true }));

        if (accountId) {
            // Create Banking Details
            updateBankAccount(hostId, connectId, accountId, values)
                .then((accounts) => {
                    notify.success('Bank account updated');
                    handleModalClose();
                    onUpdate(accounts);
                })
                .catch((error) => {
                    notify.error(error.message);
                    setModal((m) => ({ ...m, loading: false }));
                });
        } else {
            // Update Banking Details
            addBankAccount(hostId, connectId, values)
                .then((accounts) => {
                    notify.success('Bank account added');
                    handleModalClose();
                    onUpdate(accounts);
                })
                .catch((error) => {
                    notify.error(error.message);
                    setModal((m) => ({ ...m, loading: false }));
                });
        }
    };

    const handleModalDelete = (accountId) => {
        setModal((m) => ({ ...m, loading: true }));

        deleteBankAccount(hostId, connectId, accountId)
            .then((accounts) => {
                notify.success('Bank account deleted');
                handleModalClose();
                onUpdate(accounts);
            })
            .catch((error) => {
                notify.error(error.message);
                setModal((m) => ({ ...m, loading: false }));
            });
    };

    return (
        <Section>
            <Box display={'flex'} justifyContent={'space-between'} mb={2}>
                <Header>Bank Accounts</Header>
                <ActionButton disabled={readOnly} onClick={createBankingDetails}>
                    + Add
                </ActionButton>
            </Box>
            <Box mb={2}>
                <Typography>
                    This is where you will receive your funds when payouts are released. If you need
                    to change your bank account, simply edit this information. Please note we only
                    support one bank account at this time.
                </Typography>
            </Box>
            <Content>
                {bankAccounts.length === 0 ? (
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        height={100}
                    >
                        <Typography>You haven't added banking information yet</Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableBody>
                            {bankAccounts.map((account) => (
                                <TableRowClickable
                                    key={account.id}
                                    onClick={() => editBankingDetails(account)}
                                >
                                    <TableCell>{account.bankName}</TableCell>
                                    <TableCell>
                                        {account.transitNumber}
                                        {account.institutionNumber}
                                    </TableCell>
                                    <TableCell>****{account.last4}</TableCell>
                                    <TableCell>{account.currency}</TableCell>
                                    <TableCell>
                                        {account.defaultForCurrency ? (
                                            <Chip label="Default" size="small" color="secondary" />
                                        ) : (
                                            ''
                                        )}
                                    </TableCell>
                                </TableRowClickable>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Content>

            {modal.open && (
                <BankAccountModal
                    {...modal}
                    readOnly={readOnly}
                    initialAccount={bankAccounts.length === 0}
                    onClose={handleModalClose}
                    onSubmit={handleModalSubmit}
                    onDelete={handleModalDelete}
                />
            )}
        </Section>
    );
}

function BankAccountModal(props) {
    const { initialAccount, bankAccount, readOnly, onClose, onSubmit, onDelete, loading } = props;

    const isDefault = bankAccount?.defaultForCurrency;
    const editMode = Boolean(bankAccount);
    const disableSubmit = editMode && isDefault;

    const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);

    const transitNumber = useNumberInput({ integerOnly: true, maxLength: 5, disabled: readOnly });
    const institutionNumber = useNumberInput({
        integerOnly: true,
        maxLength: 3,
        disabled: readOnly,
    });
    const accountNumber1 = useNumberInput({
        integerOnly: true,
        maxLength: 12,
        disabled: readOnly,
        disablePaste: true,
    });
    const accountNumber2 = useNumberInput({
        integerOnly: true,
        maxLength: 12,
        disabled: readOnly,
        disablePaste: true,
    });
    const defaultForCurrency = useToggle({
        defaultValue: initialAccount || isDefault,
        disabled: readOnly || isDefault,
    });

    // **** Check if form is valid ***** //

    let formValid = true;

    if (editMode === false) {
        if (
            transitNumber.value.length !== 5 ||
            institutionNumber.value.length !== 3 ||
            accountNumber1.value.length < 7 ||
            accountNumber2.value.length < 7
        ) {
            formValid = false;
        }
    }

    const { notify } = useNotification();

    const handleDelete = () => {
        onDelete(bankAccount.id);
        setConfirmDeleteDialog(false);
    };

    const handleSubmit = () => {
        if (editMode) {
            // Edit Mode: We can only change the default_for_currency flag
            onSubmit(bankAccount.id, { defaultForCurrency: defaultForCurrency.checked });
        } else {
            // Add Mode: Validate all fields before submitting
            let formError = false;

            if (transitNumber.value.length !== 5) {
                transitNumber.setError('Please enter a valid number');
                formError = true;
            }
            if (institutionNumber.value.length !== 3) {
                institutionNumber.setError('Please enter a valid number');
                formError = true;
            }
            if (accountNumber1.value.length < 7) {
                accountNumber1.setError('Please enter a valid number');
                formError = true;
            } else if (accountNumber1.value !== accountNumber2.value) {
                accountNumber1.setError('Account numbers do not match');
                accountNumber2.setError('Account numbers do not match');
                formError = true;
            }

            if (formError) {
                notify.error('Please fill out all fields');
                return;
            }

            onSubmit(null, {
                transitNumber: transitNumber.value,
                institutionNumber: institutionNumber.value,
                accountNumber: accountNumber1.value,
                defaultForCurrency: defaultForCurrency.checked,
            });
        }
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <DialogTitle>{bankAccount ? 'Banking Details' : 'Add Banking Details'}</DialogTitle>
            <DialogContent>
                {editMode ? (
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                disabled
                                label="Transit Number"
                                value={bankAccount.transitNumber}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                disabled
                                label="Institution Number"
                                value={bankAccount.institutionNumber}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                label="Account Number"
                                value={`****${bankAccount.last4}`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox {...defaultForCurrency.formProps} />}
                                label={'Set as Default'}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                autoFocus={true}
                                label="Transit Number"
                                placeholder="00000"
                                {...transitNumber.formProps}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Institution Number"
                                placeholder="000"
                                {...institutionNumber.formProps}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Account Number"
                                placeholder="000000000000"
                                {...accountNumber1.formProps}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Confirm Account Number"
                                placeholder="000000000000"
                                {...accountNumber2.formProps}
                            />
                        </Grid>
                        {initialAccount === false && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Checkbox {...defaultForCurrency.formProps} />}
                                    label={'Set as Default'}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                {editMode && bankAccount.defaultForCurrency === false && (
                    <DeleteButton
                        sx={{ marginRight: 'auto' }}
                        onClick={() => setConfirmDeleteDialog(true)}
                        disabled={readOnly || loading}
                    >
                        Remove
                    </DeleteButton>
                )}
                <CancelButton onClick={onClose} disabled={loading}>
                    Cancel
                </CancelButton>
                <SubmitButton
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={readOnly || disableSubmit || loading || formValid === false}
                >
                    {editMode ? 'Update' : 'Add'}
                </SubmitButton>
            </DialogActions>

            {editMode && (
                <ConfirmAction
                    open={confirmDeleteDialog}
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmDeleteDialog(false)}
                    title={'Are you sure?'}
                    description={
                        'Your banking details will be removed. You will continue to receive payouts to your default bank account.'
                    }
                    destructive={true}
                    confirmText={'Remove'}
                />
            )}
        </Dialog>
    );
}

const TableRowClickable = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
    },
}));
